import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useLogoutApi } from '@/api/auth';
import SingleArrowNext from '@/components/Icon/SingleArrowNext';
import { menuList, mypageMenuList } from '@/constants/menu';
import { useAuthStore } from '@/store/authStore';
import { useModalStore } from '@/store/modalStore';
import { useTokenStore } from '@/store/tokenStore';

import * as S from './style';

interface MobileMenuProps {
	isOpen: boolean;
	onClickIsOpen: (isOpen: boolean) => void;
}
interface SubMenu {
	path: string;
	menu: string;
}

const MobileMenu = ({ isOpen, onClickIsOpen }: MobileMenuProps) => {
	const [openMenus, setOpenMenus] = useState<number[]>([]);
	const navigate = useNavigate();
	const { showModal } = useModalStore();
	const { isLogin } = useTokenStore();
	const { name, clearValues } = useAuthStore();
	const { mutateAsync: logout } = useLogoutApi();

	const toggleSubMenu = (index: number) => {
		openMenus.includes(index) ? setOpenMenus(openMenus.filter((item) => item !== index)) : setOpenMenus([...openMenus, index]);
	};
	const onClickCloseButton = () => {
		document.body.classList.remove('hidden');
		onClickIsOpen(false);
	};

	const onClickMenu = ({ path, menu }: SubMenu) => {
		document.body.classList.remove('hidden');
		onClickIsOpen(false);
		navigate(path ?? '', { state: { menu: menu } });
	};
	const onClickSubMenu = (path: string, title: string) => {
		if (title === '회원가입') {
			if (isLogin()) {
				onClickCloseButton();
				showModal('이미 화장품학회 회원이십니다.', 'confirm');
				return;
			} else {
				onClickMenu({ path: '/auth/signup', menu: title });
				return;
			}
		}

		if (title === '연회비 납부') {
			if (!isLogin()) {
				onClickCloseButton();
				showModal('로그인이 필요합니다.', 'login', '로그인 하러가기', () => navigate('/auth/login'));
				return;
			}
		}
		onClickMenu({ path, menu: title });
	};

	const onClickMypageMenu = (menu: string) => {
		document.body.classList.remove('hidden');
		navigate('/mypage', { state: { menu } });
		onClickIsOpen(false);
	};

	const handleLogout = async () => {
		try {
			const res = await logout();
			if (res.data.success) {
				clearValues();
				document.body.classList.remove('hidden');
				onClickIsOpen(false);
				navigate('/');
			}
		} catch (error) {
			console.error('로그아웃 중 오류가 발생했습니다:', error);
		}
	};

	const onGuestLogin = () => {
		onClickCloseButton();
		navigate('/auth/login');
	};

	const onGuestSignup = () => {
		onClickCloseButton();
		navigate('/auth/signup');
	};

	const loggedIn = isLogin();

	return (
		<S.ModalOverlay isOpen={isOpen} $loggedIn={loggedIn}>
			{!loggedIn && (
				<S.GuestPanel>
					<S.GuestTitle>회원 서비스는 로그인 후 이용하실 수 있습니다.</S.GuestTitle>
					<S.GuestActions>
						<S.GuestButton type="button" $primary onClick={onGuestLogin}>
							로그인
						</S.GuestButton>
						<S.GuestButton type="button" onClick={onGuestSignup}>
							회원가입
						</S.GuestButton>
					</S.GuestActions>
				</S.GuestPanel>
			)}
			{loggedIn && (
				<S.UserPanel>
					<S.UserPanelTop>
						<S.UserNameText title={name ? `${name} 님` : undefined}>
							<strong>{name || '회원'}</strong>
							<span> 님</span>
						</S.UserNameText>
						<S.LogoutPill type="button" onClick={handleLogout}>
							로그아웃
						</S.LogoutPill>
					</S.UserPanelTop>
				</S.UserPanel>
			)}

			<S.SectionLabel>협회 메뉴</S.SectionLabel>
			<S.SiteMenuWrap>
				<S.ModalContent>
					{menuList.map((menu, index) => (
						<div key={`${menu.mainMenu}-${index}`} className="content">
							{menu.mainPath ? (
								<S.MainMenu>
									<S.MainTextWithIcon onClick={onClickCloseButton}>
										<Link to={menu.mainPath || '/'}>{menu.mainMenu}</Link>
									</S.MainTextWithIcon>
								</S.MainMenu>
							) : (
								<S.MainMenu onClick={() => toggleSubMenu(index)}>
									{menu.mainMenu}
								</S.MainMenu>
							)}
							{menu.subMenu && openMenus.includes(index) && (
								<>
									{menu.subMenu?.map((subMenu, i) => (
										<S.SubMenu key={`${subMenu.title}-${i}`}>
											<S.TextWithIcon onClick={() => onClickSubMenu(subMenu.path ?? '', subMenu.title ?? '')}>
												<div>{subMenu.title}</div>
												<SingleArrowNext width="18" height="18" iconColor="#12539E" />
											</S.TextWithIcon>
										</S.SubMenu>
									))}
								</>
							)}
						</div>
					))}
				</S.ModalContent>
			</S.SiteMenuWrap>

			{loggedIn && (
				<>
					<S.MenuSectionDivider aria-hidden />
					<S.SectionLabel>마이페이지</S.SectionLabel>
					<S.MypageList>
						{mypageMenuList.map((item) => (
							<S.MypageLink key={item.path} type="button" onClick={() => onClickMypageMenu(item.menu)}>
								<span>{item.menu}</span>
								<SingleArrowNext width="18" height="18" iconColor="#12539E" />
							</S.MypageLink>
						))}
					</S.MypageList>
				</>
			)}
		</S.ModalOverlay>
	);
};

export default MobileMenu;
